import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";

import Breadcrumbs from "../../../components/BreadCrumbs";
import TruncatedWithTooltip from "../../../components/TruncatedWithTooltip";
import messages from "../../../assets/locale/messages";
import Filters from "../../../components/Filter";
import EmptyState from "../../../components/EmptyState";
import rolePlaceholder from "../../../assets/images/request-screen/request-placeholder.svg";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import "./Roles.scss";
import {
  UICard,
  UIButton,
  UIModal,
  UIPaginationBar,
  UIRowActionsMenu,
  UITable,
  UITextField,
} from "../../../components/ui";
import {
  createRoleRequest,
  deleteRoleRequest,
  getRolesRequest,
  getSingleRoleRequest,
  updateRoleStatusRequest,
  updateSingleRoleRequest,
} from "../../../store/Role/actions";
import { textContainOnlySpaces } from "../../../utils/Helpers";
import {
  isAdmin,
  isSuperAdmin,
  isSupervisorManger,
} from "../../../utils/Permissions";
import { useAdminModulePermission } from "../../../utils/adminPortalHelpers";
import { adminPermissionModules } from "../../../utils/Constants";
import { getPermissionRequest } from "../../../store/Auth/actions";
import store from "../../../store";

const RoleManagement = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { roles, shared } = messages[lang];
  const { id } = store.getState().auth.login.role;

  const { list, meta, _role } = useSelector((state) => state.role);
  const isLoading = useSelector((state) => state.loader);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [roleId, setRoleId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const { canAdd, canEdit, canDelete } = useAdminModulePermission(
    adminPermissionModules.rolesManagement,
  );

  const formik = useFormik({
    initialValues: {
      create_role: "",
      name_en: "",
    },
    validationSchema: Yup.object({
      create_role: Yup.string().when([], {
        is: () => createModalOpen,
        then: (schema) => schema.trim().required("roleRequired"),
        otherwise: (schema) => schema,
      }),
      name_en: Yup.string().when([], {
        is: () => editModalOpen,
        then: (schema) => schema.trim().required("roleNameRequired"),
        otherwise: (schema) => schema,
      }),
    }),
    onSubmit: ({ create_role, name_en }) => {
      if (createModalOpen) {
        const trimmedRole = create_role.trim();
        if (!trimmedRole) {
          formik.setFieldError("create_role", "roleRequired");
          return;
        }
        dispatch(
          createRoleRequest({
            name_en: trimmedRole,
            name_ar: trimmedRole,
          })
        );
        formik.resetForm();
        setCreateModalOpen(false);
        return;
      }

      if (editModalOpen && roleId) {
        dispatch(
          updateSingleRoleRequest({
            id: roleId,
            params: {
              name_en,
              name_ar: name_en,
            },
          })
        );
        formik.resetForm();
        setEditModalOpen(false);
        setRoleId(null);
      }
    },
  });

  const getRoles = useCallback(
    (pageNo) => {
      dispatch(getRolesRequest({ page: pageNo, name: searchValue }));
    },
    [dispatch, searchValue]
  );

  useEffect(() => {
    if (!isSuperAdmin() && !isAdmin() && !isSupervisorManger()) {
      dispatch(getPermissionRequest({ id }));
    }
  }, [lang, dispatch, id]);

  useEffect(() => {
    getRoles(page);
  }, [page, getRoles, location.pathname]);

  useEffect(() => {
    if (
      (searchValue.length >= 3 || searchValue.length === 0) &&
      !textContainOnlySpaces(searchValue)
    ) {
      setPage(1);
      getRoles(1);
    }
  }, [searchValue, getRoles]);

  useEffect(() => {
    if (_role && editModalOpen) {
      formik.setFieldValue("name_en", _role.name || "");
    }
  }, [_role, editModalOpen]);

  const handleMenuAction = (option, rowId) => {
    if (option === roles.listRoles.actionOptions.view) {
      history.push(ROUTE_PATHS.viewRoleManagement.replace(":id", rowId));
      return;
    }
    dispatch(updateRoleStatusRequest({ id: rowId }));
  };

  const handleDeleteRoleModal = (rowId) => {
    setRoleId(rowId);
    setDeleteModalOpen(true);
  };

  const handleDeleteRole = () => {
    if (roleId) {
      dispatch(deleteRoleRequest({ id: roleId }));
    }
    setDeleteModalOpen(false);
    setRoleId(null);
  };

  const handleEditRole = (rowId) => {
    setRoleId(rowId);
    dispatch(getSingleRoleRequest({ id: rowId }));
    setEditModalOpen(true);
  };

  const handleCreateClose = () => {
    setCreateModalOpen(false);
    formik.resetForm();
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setRoleId(null);
    formik.resetForm();
  };

  const canDeleteRow = useCallback(
    (row) =>
      (row?.users_count === 0 || row?.users_count === "0") && canDelete,
    [canDelete]
  );

  const rolesList = Array.isArray(list) ? list : [];

  const rows = useMemo(
    () =>
      rolesList.map((row) => ({
        ...row,
        id: row.id,
      })),
    [rolesList]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        field: "name",
        headerName: roles.listRoles.roles,
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
          const value = params.value ?? "-";
          if (value === "-") {
            return <span className="MuiDataGrid-cellContent">-</span>;
          }
          return (
            <TruncatedWithTooltip title={value}>
              <span className="MuiDataGrid-cellContent">{value}</span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "users_count",
        headerName: roles.listRoles.numberOfUser,
        flex: 0.6,
        minWidth: 110,
        sortable: false,
        renderCell: (params) => (
          <span className="MuiDataGrid-cellContent">{params.value ?? "-"}</span>
        ),
      },
      {
        field: "status",
        headerName: roles.listRoles.status,
        flex: 0.6,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => (
          <span className="MuiDataGrid-cellContent">{params.value ?? "-"}</span>
        ),
      },
      {
        field: "actions",
        headerName: roles.listRoles.action,
        width: 90,
        minWidth: 80,
        flex: 0,
        sortable: false,
        align: "center",
        headerAlign: "center",
        cellClassName: "roles-list-view__actions-cell",
        renderCell: (params) => {
          const items = [
            {
              key: "view",
              label: roles.listRoles.actionOptions.view,
              onClick: () =>
                handleMenuAction(
                  roles.listRoles.actionOptions.view,
                  params.row.id
                ),
            },
          ];

          if (canEdit) {
            items.push({
              key: "toggleStatus",
              label:
                params.row?.status === "disabled"
                  ? roles.listRoles.actionOptions.enabled
                  : roles.listRoles.actionOptions.disabled,
              onClick: () =>
                handleMenuAction(
                  params.row?.status === "disabled"
                    ? roles.listRoles.actionOptions.enabled
                    : roles.listRoles.actionOptions.disabled,
                  params.row.id
                ),
            });
            items.push({
              key: "edit",
              label: shared.edit,
              onClick: () => handleEditRole(params.row.id),
            });
          }

          if (canDeleteRow(params.row)) {
            items.push({
              key: "delete",
              label: roles.listRoles.actionOptions.delete,
              danger: true,
              onClick: () => handleDeleteRoleModal(params.row.id),
            });
          }

          return (
            <UIRowActionsMenu
              ariaLabel={roles.listRoles.action}
              items={items}
            />
          );
        },
      },
    ];

    return cols;
  }, [roles, shared, canEdit, canDelete, canDeleteRow]);

  const currentPage = meta?.currentPage || page;
  const paginationFrom =
    meta?.count > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(currentPage * pageSize, meta?.count || 0);
  const paginationPages = meta?.pages || 1;

  const renderRolesList = () => {
    if (!isLoading && !rows.length) {
      return (
        <UICard>
          <EmptyState
            description={roles.listRoles.placeholder.noRoleFound}
            imgSrc={rolePlaceholder}
            showActionButton={false}
          />
        </UICard>
      );
    }

    return (
      <>
        <UITable
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          contentSized
          hideFooter
          disableSelectionOnClick
          onRowClick={({ row }) =>
            history.push(ROUTE_PATHS.viewRoleManagement.replace(":id", row.id))
          }
        />
        {(meta?.count || 0) > 0 && (
          <UIPaginationBar
            currentPage={currentPage}
            pages={paginationPages}
            pageSize={pageSize}
            rangeTemplate={roles.listRoles.showingRange}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={meta?.count || 0}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={setPage}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPage(1);
            }}
            className="roles-list-view__pagination"
          />
        )}
      </>
    );
  };

  const renderModalActions = (onCancel, submitDisabled = false) => (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="center"
      width="100%"
      className="roles-list-view__modal-actions"
    >
      <UIButton
        appearance="gradient"
        type="button"
        disabled={submitDisabled}
        onClick={() => formik.submitForm()}
      >
        {shared.confirm}
      </UIButton>
      <UIButton
        variant="outlined"
        className="roles-list-view__cancel-btn"
        onClick={onCancel}
      >
        {shared.cancel}
      </UIButton>
    </Stack>
  );

  return (
    <div className="roles-list-view">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[
            {
              path: "",
              label: roles.listBreadCrumbs.roleManagement,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
        <Box className="roles-list-view__header">
          <Box>
            <Typography variant="h5">
              {roles.listRoles.roleManagement.trim()}
            </Typography>
            <Typography className="roles-list-view__description">
              {roles.listRoles.roleDescription}
            </Typography>
          </Box>
          {canAdd && (
            <UIButton
              appearance="gradient"
              type="button"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setCreateModalOpen(true)}
            >
              {roles.listFilter.createRole}
            </UIButton>
          )}
        </Box>
      </div>

      <Filters
        searchInput={{
          placeholder: roles.listFilter.searchRole,
          name: "search",
          value: searchValue,
          onChange: setSearchValue,
        }}
        filters={[]}
        classNames={{
          container: "roles-list-view__filters",
          base: "",
          search: "roles-list-view__search mb-3 mb-md-0",
          filter: "",
        }}
      />

      <div className="roles-list-view__content d-flex flex-column justify-content-between">
        {renderRolesList()}
      </div>

      <UIModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setRoleId(null);
        }}
        title={roles.listRoles.deleteRole}
        maxWidth="xs"
        showClose={false}
        actions={
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            width="100%"
            className="roles-list-view__modal-actions"
          >
            <UIButton
              appearance="gradient"
              onClick={handleDeleteRole}
            >
              {shared.confirm}
            </UIButton>
            <UIButton
              variant="outlined"
              className="roles-list-view__cancel-btn"
              onClick={() => {
                setDeleteModalOpen(false);
                setRoleId(null);
              }}
            >
              {shared.cancel}
            </UIButton>
          </Stack>
        }
      >
        <Typography className="roles-list-view__modal-message">
          {roles.listRoles.areYouSure}
        </Typography>
      </UIModal>

      <UIModal
        open={createModalOpen}
        onClose={handleCreateClose}
        title={roles.listRoles.addNewRole}
        maxWidth="sm"
        actions={renderModalActions(handleCreateClose)}
      >
        <form onSubmit={formik.handleSubmit}>
          <UITextField
            id="create-role"
            name="create_role"
            label={roles.listRoles.role}
            placeholder={roles.listRoles.placeholder.rolePlaceholder}
            required
            value={formik.values.create_role}
            onValueChange={(value) => {
              formik.setFieldTouched("create_role", true);
              formik.setFieldValue("create_role", value);
            }}
            onBlur={() => formik.setFieldTouched("create_role", true)}
            error={Boolean(
              formik.touched.create_role && formik.errors.create_role
            )}
            helperText={
              formik.touched.create_role ? formik.errors.create_role : ""
            }
          />
        </form>
      </UIModal>

      <UIModal
        open={editModalOpen}
        onClose={handleEditClose}
        title={roles.listRoles.editRole}
        maxWidth="sm"
        actions={renderModalActions(
          handleEditClose,
          !formik.dirty || !formik.isValid
        )}
      >
        <form onSubmit={formik.handleSubmit}>
          <UITextField
            id="name_en"
            name="name_en"
            label={roles.listRoles.roleEn}
            placeholder={roles.listRoles.placeholder.rolePlaceholderEn}
            required
            value={formik.values.name_en}
            onValueChange={(value) => {
              formik.setFieldTouched("name_en", true);
              formik.setFieldValue("name_en", value);
            }}
            onBlur={() => formik.setFieldTouched("name_en", true)}
            error={Boolean(formik.touched.name_en && formik.errors.name_en)}
            helperText={formik.touched.name_en ? formik.errors.name_en : ""}
          />
        </form>
      </UIModal>
    </div>
  );
};

export default RoleManagement;
